/**
 * Transport constants and helpers.
 * Extracted from WanderAppComplete.jsx for shared use by web and mobile.
 */

// ─── Airport IATA → City Mapping ────────────────────────────────────────────

export const DEPARTURE_AIRPORT_CITY_MAP = {
  // Japan
  'HND': 'Tokyo', 'NRT': 'Tokyo', 'KIX': 'Osaka', 'ITM': 'Osaka',
  'NGO': 'Nagoya', 'FUK': 'Fukuoka', 'CTS': 'Sapporo', 'OKA': 'Okinawa',
  'HIJ': 'Hiroshima', 'SDJ': 'Sendai', 'KOJ': 'Kagoshima',
  // Europe
  'CDG': 'Paris', 'ORY': 'Paris', 'LHR': 'London', 'LGW': 'London', 'STN': 'London',
  'FCO': 'Rome', 'CIA': 'Rome', 'MXP': 'Milan', 'LIN': 'Milan', 'BGY': 'Milan',
  'BCN': 'Barcelona', 'MAD': 'Madrid',
  'AMS': 'Amsterdam', 'FRA': 'Frankfurt', 'MUC': 'Munich', 'VIE': 'Vienna',
  'ZRH': 'Zurich', 'GVA': 'Geneva', 'LIS': 'Lisbon', 'ATH': 'Athens',
  'IST': 'Istanbul', 'CPH': 'Copenhagen', 'ARN': 'Stockholm', 'OSL': 'Oslo',
  'HEL': 'Helsinki', 'PRG': 'Prague', 'BUD': 'Budapest', 'WAW': 'Warsaw',
  // Italy secondary airports (PR #235 added these to MULTI_AIRPORT_CITIES
  // but the reverse map was missing — JFK→BLQ used to fall to ~4h default).
  'FLR': 'Florence', 'PSA': 'Pisa', 'VCE': 'Venice', 'TSF': 'Venice',
  'NAP': 'Naples', 'BLQ': 'Bologna', 'VRN': 'Verona', 'TRN': 'Turin',
  'GOA': 'Genoa', 'CTA': 'Catania', 'PMO': 'Palermo',
  // France secondary
  'NCE': 'Nice', 'LYS': 'Lyon', 'MRS': 'Marseille', 'TLS': 'Toulouse',
  // Spain secondary
  'AGP': 'Málaga', 'SVQ': 'Seville', 'BIO': 'Bilbao', 'VLC': 'Valencia',
  // Germany secondary
  'TXL': 'Berlin', 'BER': 'Berlin', 'HAM': 'Hamburg', 'DUS': 'Düsseldorf',
  'CGN': 'Cologne', 'STR': 'Stuttgart',
  // UK secondary
  'MAN': 'Manchester', 'EDI': 'Edinburgh', 'GLA': 'Glasgow', 'BHX': 'Birmingham',
  'LPL': 'Liverpool', 'BFS': 'Belfast', 'BRS': 'Bristol',
  // Greek islands (PR #235 territory)
  'JTR': 'Santorini', 'JMK': 'Mykonos', 'HER': 'Heraklion', 'RHO': 'Rhodes',
  // SARs / dependencies often confused with parent country
  'MFM': 'Macau',
  // NZ secondary
  'ROT': 'Rotorua', 'HLZ': 'Hamilton', 'NPE': 'Napier', 'NSN': 'Nelson',
  'TRG': 'Tauranga', 'PMR': 'Palmerston North', 'BHE': 'Blenheim',
  // Mexico
  'CUN': 'Cancun', 'OAX': 'Oaxaca', 'TLC': 'Toluca', 'GDL': 'Guadalajara',
  'PVR': 'Puerto Vallarta', 'SJD': 'Cabo San Lucas', 'BJX': 'Bajío',
  'MID': 'Mérida', 'TQO': 'Tulum',
  // Note: do NOT map 'TLM' here — TLM is Tlemcen, Algeria. Tulum's actual
  // airport is TQO (new, opened 2023). Older itineraries that flew into
  // Cancún (CUN) and drove still use CUN for Tulum trips.
  // Americas
  'JFK': 'New York', 'LGA': 'New York', 'EWR': 'New York',
  'LAX': 'Los Angeles', 'SFO': 'San Francisco', 'MIA': 'Miami',
  'ORD': 'Chicago', 'DFW': 'Dallas', 'ATL': 'Atlanta', 'DEN': 'Denver',
  'SEA': 'Seattle', 'BOS': 'Boston', 'IAD': 'Washington DC', 'DCA': 'Washington DC',
  'YYZ': 'Toronto', 'YVR': 'Vancouver', 'MEX': 'Mexico City',
  'GRU': 'São Paulo', 'GIG': 'Rio de Janeiro', 'EZE': 'Buenos Aires',
  'SCL': 'Santiago', 'BOG': 'Bogotá', 'LIM': 'Lima',
  // Asia-Pacific
  'ICN': 'Seoul', 'GMP': 'Seoul', 'PEK': 'Beijing', 'PVG': 'Shanghai',
  'SIN': 'Singapore', 'BKK': 'Bangkok', 'HKG': 'Hong Kong',
  'TPE': 'Taipei', 'KUL': 'Kuala Lumpur', 'CGK': 'Jakarta',
  'DEL': 'Delhi', 'BOM': 'Mumbai', 'MNL': 'Manila',
  // Middle East / Africa / Oceania
  'DXB': 'Dubai', 'DOH': 'Doha', 'AUH': 'Abu Dhabi',
  'SYD': 'Sydney', 'MEL': 'Melbourne', 'AKL': 'Auckland',
  'CHC': 'Christchurch', 'WLG': 'Wellington', 'ZQN': 'Queenstown', 'DUD': 'Dunedin',
  'CPT': 'Cape Town', 'JNB': 'Johannesburg', 'CAI': 'Cairo',
  // Polar expedition gateway cities (Antarctica, Arctic)
  'PUQ': 'Punta Arenas', 'USH': 'Ushuaia', 'LYR': 'Longyearbyen', 'TOS': 'Tromsø',
  // Galapagos gateway
  'UIO': 'Quito', 'GYE': 'Guayaquil',
};

// ─── Train-Friendly Airport Routes ──────────────────────────────────────────
// City pairs where high-speed rail is the preferred mode for hotel→airport transit

export const TRAIN_FRIENDLY_AIRPORT_ROUTES = [
  // Japan (Shinkansen — bullet train network)
  ['kyoto', 'nagoya'], ['kyoto', 'osaka'], ['kyoto', 'tokyo'],
  ['osaka', 'nagoya'], ['osaka', 'tokyo'], ['osaka', 'hiroshima'],
  ['tokyo', 'nagoya'], ['tokyo', 'sendai'], ['tokyo', 'nagano'],
  ['nagoya', 'tokyo'], ['nagoya', 'osaka'],
  ['hiroshima', 'osaka'], ['hiroshima', 'fukuoka'],
  ['fukuoka', 'hiroshima'], ['fukuoka', 'osaka'],
  ['nara', 'osaka'], ['nara', 'nagoya'], ['nara', 'kyoto'],
  ['kobe', 'osaka'], ['kobe', 'nagoya'],
  ['kanazawa', 'tokyo'], ['kanazawa', 'nagoya'],
  ['okayama', 'osaka'], ['okayama', 'hiroshima'],
  // South Korea (KTX)
  ['busan', 'seoul'], ['daegu', 'seoul'], ['daejeon', 'seoul'],
  // Taiwan (HSR)
  ['kaohsiung', 'taipei'], ['taichung', 'taipei'], ['tainan', 'taipei'],
  // China (CRH)
  ['shanghai', 'beijing'], ['shanghai', 'nanjing'], ['guangzhou', 'shenzhen'],
  // Switzerland
  ['zermatt', 'geneva'], ['zermatt', 'zurich'], ['interlaken', 'zurich'], ['interlaken', 'geneva'],
  ['lucerne', 'zurich'], ['st. moritz', 'zurich'], ['grindelwald', 'zurich'],
  // France (TGV)
  ['chamonix', 'geneva'], ['annecy', 'geneva'], ['lyon', 'paris'],
  ['marseille', 'paris'], ['bordeaux', 'paris'], ['nice', 'paris'],
  ['strasbourg', 'paris'], ['lille', 'paris'], ['avignon', 'paris'],
  // Italy (Frecciarossa)
  ['florence', 'rome'], ['venice', 'rome'], ['milan', 'rome'],
  ['naples', 'rome'], ['bologna', 'rome'], ['turin', 'milan'],
  ['venice', 'milan'], ['florence', 'milan'], ['florence', 'venice'],
  // Spain (AVE)
  ['barcelona', 'madrid'], ['seville', 'madrid'], ['valencia', 'madrid'],
  ['malaga', 'madrid'], ['cordoba', 'madrid'],
  // Austria / Germany
  ['innsbruck', 'munich'], ['salzburg', 'munich'], ['salzburg', 'vienna'],
  ['munich', 'frankfurt'], ['berlin', 'frankfurt'],
  ['cologne', 'frankfurt'], ['hamburg', 'berlin'], ['nuremberg', 'munich'],
  ['vienna', 'munich'], ['vienna', 'prague'],
  // Benelux
  ['brussels', 'amsterdam'], ['brussels', 'paris'], ['amsterdam', 'paris'],
  // UK
  ['edinburgh', 'london'], ['manchester', 'london'], ['birmingham', 'london'],
  ['york', 'london'], ['bristol', 'london'],
  // Scandinavia
  ['gothenburg', 'stockholm'], ['malmö', 'copenhagen'],
];

// ─── Multi-Airport Cities ───────────────────────────────────────────────────

export const MULTI_AIRPORT_CITIES = {
  'New York': [
    { code: 'JFK', name: 'John F. Kennedy International', priority: 1, notes: 'Primary international hub' },
    { code: 'LGA', name: 'LaGuardia', priority: 2, notes: 'Domestic + some international' },
    { code: 'EWR', name: 'Newark Liberty International', priority: 3, notes: 'Across the Hudson' },
    { code: 'HPN', name: 'Westchester County', priority: 4, notes: 'Regional / private (~45 min north of NYC)' }
  ],
  'NYC': [
    { code: 'JFK', name: 'John F. Kennedy International', priority: 1 },
    { code: 'LGA', name: 'LaGuardia', priority: 2 },
    { code: 'EWR', name: 'Newark Liberty International', priority: 3 },
    { code: 'HPN', name: 'Westchester County', priority: 4 }
  ],
  'London': [
    { code: 'LHR', name: 'Heathrow', priority: 1 },
    { code: 'LGW', name: 'Gatwick', priority: 2 },
    { code: 'STN', name: 'Stansted', priority: 3 },
    { code: 'LTN', name: 'Luton', priority: 4 },
    { code: 'LCY', name: 'London City', priority: 5 }
  ],
  'Tokyo': [
    { code: 'NRT', name: 'Narita International', priority: 1 },
    { code: 'HND', name: 'Haneda', priority: 2 }
  ],
  'Paris': [
    { code: 'CDG', name: 'Charles de Gaulle', priority: 1 },
    { code: 'ORY', name: 'Orly', priority: 2 }
  ],
  'Chicago': [
    { code: 'ORD', name: "O'Hare International", priority: 1 },
    { code: 'MDW', name: 'Midway International', priority: 2 }
  ],
  'Los Angeles': [
    { code: 'LAX', name: 'Los Angeles International', priority: 1 },
    { code: 'BUR', name: 'Hollywood Burbank', priority: 2, notes: 'Closer to the Valley' },
    { code: 'LGB', name: 'Long Beach', priority: 3 },
    { code: 'SNA', name: 'John Wayne / Orange County', priority: 3 },
    { code: 'ONT', name: 'Ontario International', priority: 4, notes: 'Inland Empire' }
  ],
  'San Francisco': [
    { code: 'SFO', name: 'San Francisco International', priority: 1 },
    { code: 'OAK', name: 'Oakland International', priority: 2 },
    { code: 'SJC', name: 'San Jose International', priority: 3 }
  ],
  'Washington': [
    { code: 'DCA', name: 'Ronald Reagan Washington National', priority: 1, notes: 'Closest to downtown' },
    { code: 'IAD', name: 'Dulles International', priority: 2 },
    { code: 'BWI', name: 'Baltimore/Washington International', priority: 3 }
  ],
  'Washington DC': [
    { code: 'DCA', name: 'Ronald Reagan Washington National', priority: 1 },
    { code: 'IAD', name: 'Dulles International', priority: 2 },
    { code: 'BWI', name: 'Baltimore/Washington International', priority: 3 }
  ],
  'Houston': [
    { code: 'IAH', name: 'George Bush Intercontinental', priority: 1, notes: 'Primary international' },
    { code: 'HOU', name: 'William P. Hobby', priority: 2 }
  ],
  'Miami': [
    { code: 'MIA', name: 'Miami International', priority: 1 },
    { code: 'FLL', name: 'Fort Lauderdale-Hollywood', priority: 2 }
  ],
  'Boston': [
    { code: 'BOS', name: 'Logan International', priority: 1 },
    { code: 'PVD', name: 'T. F. Green (Providence)', priority: 2 },
    { code: 'MHT', name: 'Manchester-Boston Regional', priority: 3 }
  ],
  'Milan': [
    { code: 'MXP', name: 'Malpensa', priority: 1 },
    { code: 'LIN', name: 'Linate', priority: 2 },
    { code: 'BGY', name: 'Bergamo Orio al Serio', priority: 3, notes: 'Low-cost hub' }
  ],
  // Italian cities the radius search should surface as alternatives to
  // each other. Listed even when single-airport so the picker offers
  // them to nearby cities (Bologna→FLR/VCE/PSA, Florence→BLQ/PSA, etc.).
  'Rome': [
    { code: 'FCO', name: 'Fiumicino', priority: 1 },
    { code: 'CIA', name: 'Ciampino', priority: 2, notes: 'Low-cost' }
  ],
  'Florence': [
    { code: 'FLR', name: 'Florence Peretola', priority: 1 },
    { code: 'PSA', name: 'Pisa Galileo Galilei', priority: 2, notes: '~1h drive — often cheaper' }
  ],
  'Venice': [
    { code: 'VCE', name: 'Marco Polo', priority: 1 },
    { code: 'TSF', name: 'Treviso', priority: 2, notes: 'Low-cost, ~30 min from Venice' }
  ],
  'Bologna': [
    { code: 'BLQ', name: 'Marconi', priority: 1 }
  ],
  'Naples': [
    { code: 'NAP', name: 'Naples Capodichino', priority: 1 }
  ],
  'Pisa': [
    { code: 'PSA', name: 'Pisa Galileo Galilei', priority: 1 },
    { code: 'FLR', name: 'Florence Peretola', priority: 2, notes: '~1h drive' }
  ],
  'Verona': [
    { code: 'VRN', name: 'Verona Villafranca', priority: 1 }
  ],
  'Turin': [
    { code: 'TRN', name: 'Turin Caselle', priority: 1 }
  ],
  'Bangkok': [
    { code: 'BKK', name: 'Suvarnabhumi', priority: 1 },
    { code: 'DMK', name: 'Don Mueang', priority: 2 }
  ],
  'Moscow': [
    { code: 'SVO', name: 'Sheremetyevo', priority: 1 },
    { code: 'DME', name: 'Domodedovo', priority: 2 },
    { code: 'VKO', name: 'Vnukovo', priority: 3 }
  ],
  // Resort / multi-airport mountain-town serving areas (T3)
  'Aspen': [
    { code: 'ASE', name: 'Aspen/Pitkin County', priority: 1, notes: 'In town; small, weather-dependent' },
    { code: 'EGE', name: 'Eagle County Regional', priority: 2, notes: '~70 mi / ~40 min — often cheaper, more options' },
    { code: 'DEN', name: 'Denver International', priority: 3, notes: '~200 mi / ~3.5h — widest route network' }
  ],
  'Vail': [
    { code: 'EGE', name: 'Eagle County Regional', priority: 1, notes: '~35 min to Vail' },
    { code: 'DEN', name: 'Denver International', priority: 2, notes: '~2h drive' }
  ],
  'Park City': [
    { code: 'SLC', name: 'Salt Lake City International', priority: 1, notes: 'Salt Lake City, 35mi/40min' }
  ],
  'Deer Valley': [
    { code: 'SLC', name: 'Salt Lake City International', priority: 1, notes: '~40 min drive' }
  ],
  'Telluride': [
    { code: 'MTJ', name: 'Montrose Regional', priority: 1, notes: 'Year-round, 65mi/1.5h drive — most flights' },
    { code: 'TEX', name: 'Telluride Regional', priority: 2, notes: 'In town, seasonal, weather-dependent' },
    { code: 'DEN', name: 'Denver International', priority: 3, notes: '~6h drive — widest routes' }
  ],
  'Pagosa Springs': [
    { code: 'DRO', name: 'Durango-La Plata County', priority: 1, notes: 'Durango-La Plata, 60mi/1h' },
    { code: 'ABQ', name: 'Albuquerque International Sunport', priority: 2, notes: '~3.5h drive — more routes' }
  ],
  'Jackson Hole': [
    { code: 'JAC', name: 'Jackson Hole Airport', priority: 1, notes: 'In town' },
    { code: 'IDA', name: 'Idaho Falls Regional', priority: 2, notes: 'Idaho Falls ~2h drive' },
    { code: 'SLC', name: 'Salt Lake City International', priority: 3, notes: 'Salt Lake City ~5h drive' }
  ],
  'Sun Valley': [
    { code: 'SUN', name: 'Friedman Memorial Airport', priority: 1, notes: 'Friedman Memorial, 15mi' },
    { code: 'BOI', name: 'Boise Airport', priority: 2, notes: 'Boise ~3h drive' }
  ],
  'Whistler': [
    { code: 'YVR', name: 'Vancouver International Airport', priority: 1, notes: 'Vancouver, 75mi/2h drive' }
  ]
};

/**
 * Detect city from a location string using multi-airport cities data.
 * @param {string} location
 * @returns {{ city: string, airports: Array }|null}
 */
export function getCityFromLocation(location) {
  if (!location) return null;
  const cityMatch = location.match(/^([^,(]+)/);
  if (!cityMatch) return null;
  const city = cityMatch[1].trim();
  for (const [key, airports] of Object.entries(MULTI_AIRPORT_CITIES)) {
    if (city.toLowerCase().includes(key.toLowerCase()) || key.toLowerCase().includes(city.toLowerCase())) {
      return { city: key, airports };
    }
  }
  return null;
}

// ─── City to Airport Code Lookup ────────────────────────────────────────────

export const CITY_TO_AIRPORT_CODE = {
  'new york': 'JFK', 'new york city': 'JFK', 'nyc': 'JFK',
  'los angeles': 'LAX', 'chicago': 'ORD', 'san francisco': 'SFO',
  'miami': 'MIA', 'boston': 'BOS', 'seattle': 'SEA', 'washington': 'IAD',
  'london': 'LHR', 'paris': 'CDG', 'rome': 'FCO', 'barcelona': 'BCN',
  'madrid': 'MAD', 'berlin': 'BER', 'amsterdam': 'AMS', 'lisbon': 'LIS',
  // Alt-language spellings (resolvers sometimes return native names)
  'lisboa': 'LIS',              // Portuguese for Lisbon
  'firenze': 'FLR',             // Italian for Florence
  'roma': 'FCO',                // Italian for Rome
  'venezia': 'VCE',             // Italian for Venice
  'napoli': 'NAP',              // Italian for Naples
  'milano': 'MXP',              // Italian for Milan
  'sevilla': 'SVQ',             // Spanish for Seville
  'malága': 'AGP', 'málaga': 'AGP',
  'munchen': 'MUC', 'münchen': 'MUC',  // Munich
  'wien': 'VIE',                // Vienna
  'praha': 'PRG',               // Prague
  'moskva': 'SVO', 'moscow': 'SVO',
  'kobenhavn': 'CPH', 'københavn': 'CPH', // Copenhagen
  'athina': 'ATH',              // Athens
  'tokyo': 'NRT', 'osaka': 'KIX', 'bangkok': 'BKK', 'singapore': 'SIN',
  'hong kong': 'HKG', 'seoul': 'ICN', 'sydney': 'SYD', 'melbourne': 'MEL',
  'dubai': 'DXB', 'istanbul': 'IST', 'athens': 'ATH', 'zurich': 'ZRH',
  'vienna': 'VIE', 'prague': 'PRG', 'budapest': 'BUD', 'copenhagen': 'CPH',
  'stockholm': 'ARN', 'oslo': 'OSL', 'helsinki': 'HEL', 'dublin': 'DUB',
  'edinburgh': 'EDI', 'munich': 'MUC', 'milan': 'MXP', 'venice': 'VCE',
  'florence': 'FLR', 'naples': 'NAP', 'nice': 'NCE', 'antibes': 'NCE',
  'cannes': 'NCE', 'marseille': 'MRS', 'lyon': 'LYS', 'bordeaux': 'BOD',
  'toronto': 'YYZ', 'vancouver': 'YVR', 'montreal': 'YUL',
  'mexico city': 'MEX', 'cancun': 'CUN', 'oaxaca': 'OAX', 'oaxaca city': 'OAX',
  'guadalajara': 'GDL', 'puerto vallarta': 'PVR', 'cabo san lucas': 'SJD', 'los cabos': 'SJD',
  'tulum': 'CUN', 'playa del carmen': 'CUN', 'merida': 'MID', 'monterrey': 'MTY',
  // Central America + Caribbean
  'belize city': 'BZE', 'belize': 'BZE',
  'ambergris caye': 'SPR', 'san pedro': 'SPR',
  'caye caulker': 'CUK',
  'placencia': 'PLJ',
  'san ignacio': 'BZE', // nearest intl hub
  'roatan': 'RTB', 'honduras': 'TGU',
  'san jose': 'SJO', 'san jose costa rica': 'SJO', 'liberia costa rica': 'LIR',
  'panama city': 'PTY', 'panama': 'PTY',
  'antigua guatemala': 'GUA', 'guatemala city': 'GUA', 'guatemala': 'GUA',
  'havana': 'HAV', 'cuba': 'HAV',
  'montego bay': 'MBJ', 'kingston': 'KIN', 'ocho rios': 'MBJ',
  'san juan': 'SJU', 'puerto rico': 'SJU',
  'nassau': 'NAS', 'bahamas': 'NAS',
  'punta cana': 'PUJ', 'santo domingo': 'SDQ',
  'aruba': 'AUA', 'oranjestad': 'AUA',
  'curacao': 'CUR', 'willemstad': 'CUR',
  // Antigua + Barbuda
  'antigua': 'ANU', 'antigua and barbuda': 'ANU', 'st johns antigua': 'ANU', "st john's antigua": 'ANU',
  'hermitage bay': 'ANU', 'jumby bay': 'ANU', 'jumby bay island': 'ANU',
  // Saint Lucia + Saint Vincent
  'saint lucia': 'UVF', 'st lucia': 'UVF', 'castries': 'UVF',
  'saint vincent': 'SVD', 'st vincent': 'SVD', 'kingstown saint vincent': 'SVD',
  // Grenada, Barbados, Saint Kitts & Nevis
  'grenada': 'GND',
  'barbados': 'BGI', 'bridgetown': 'BGI',
  'saint kitts': 'SKB', 'st kitts': 'SKB', 'basseterre': 'SKB',
  'nevis': 'NEV',
  // Turks & Caicos, Cayman Islands, BVI
  'turks and caicos': 'PLS', 'providenciales': 'PLS',
  'grand cayman': 'GCM', 'cayman islands': 'GCM', 'george town cayman': 'GCM',
  'tortola': 'EIS', 'british virgin islands': 'EIS', 'road town': 'EIS',
  // US Virgin Islands
  'st thomas': 'STT', 'saint thomas': 'STT', 'st croix': 'STX', 'saint croix': 'STX',
  // Anguilla, Dominica, Martinique, Guadeloupe
  'anguilla': 'AXA',
  'dominica': 'DOM', 'roseau': 'DOM',
  'martinique': 'FDF', 'fort de france': 'FDF',
  'guadeloupe': 'PTP', 'pointe a pitre': 'PTP',
  // Sint Maarten / Saint Martin
  'sint maarten': 'SXM', 'st maarten': 'SXM', 'saint martin': 'SXM', 'st martin': 'SXM',
  // Azores + Madeira
  'azores': 'PDL', 'ponta delgada': 'PDL', 'sao miguel': 'PDL', 'são miguel': 'PDL',
  'terceira': 'TER', 'angra do heroismo': 'TER',
  'faial': 'HOR', 'horta': 'HOR',
  'pico': 'PIX',
  'madeira': 'FNC', 'funchal': 'FNC',
  'rio de janeiro': 'GIG',
  'sao paulo': 'GRU', 'buenos aires': 'EZE', 'lima': 'LIM',
  // US ski / mountain towns — map to nearest major airport
  'park city': 'SLC', 'park city utah': 'SLC', 'park city, ut': 'SLC',
  'deer valley': 'SLC', 'montage deer valley': 'SLC',
  'salt lake city': 'SLC', 'slc': 'SLC',
  'aspen': 'ASE', 'aspen colorado': 'ASE', 'aspen, co': 'ASE',
  'snowmass': 'ASE', 'snowmass village': 'ASE',
  'vail': 'EGE', 'beaver creek': 'EGE', 'eagle': 'EGE', 'eagle vail': 'EGE',
  // Telluride → MTJ (Montrose, year-round, primary) over TEX (in-town, seasonal/weather-dependent)
  'telluride': 'MTJ', 'breckenridge': 'DEN', 'keystone': 'DEN',
  // Pagosa Springs → DRO (Durango-La Plata, ~1h) over ABQ (~3.5h alt)
  'pagosa springs': 'DRO', 'pagosa': 'DRO',
  // Whistler → YVR (Vancouver, ~2h drive)
  'whistler': 'YVR',
  'jackson hole': 'JAC', 'jackson wyoming': 'JAC', 'teton village': 'JAC',
  'big sky': 'BZN', 'bozeman': 'BZN',
  'sun valley': 'SUN', 'ketchum': 'SUN',
  'lake tahoe': 'RNO', 'tahoe': 'RNO', 'south lake tahoe': 'RNO',
  'mammoth': 'MMH', 'mammoth lakes': 'MMH',
  'moab': 'CNY', 'zion': 'SGU', 'springdale': 'SGU',
  'sedona': 'FLG', 'flagstaff': 'FLG',
  'cairo': 'CAI', 'sharm el sheikh': 'SSH', 'sharm el-sheikh': 'SSH', 'sharm': 'SSH',
  'hurghada': 'HRG', 'luxor': 'LXR', 'aswan': 'ASW', 'alexandria': 'HBE',
  'cape town': 'CPT', 'johannesburg': 'JNB',
  'nairobi': 'NBO', 'marrakech': 'RAK', 'casablanca': 'CMN',
  'bali': 'DPS', 'denpasar': 'DPS', 'jakarta': 'CGK', 'kuala lumpur': 'KUL',
  'hanoi': 'HAN', 'ho chi minh': 'SGN', 'manila': 'MNL',
  'mumbai': 'BOM', 'delhi': 'DEL', 'new delhi': 'DEL',
  'beijing': 'PEK', 'shanghai': 'PVG', 'taipei': 'TPE',
  'honolulu': 'HNL', 'maui': 'OGG', 'maldives': 'MLE', 'male': 'MLE',
  'phuket': 'HKT', 'chiang mai': 'CNX', 'kyoto': 'KIX',
  'santorini': 'JTR', 'mykonos': 'JMK', 'crete': 'HER',
  'dubrovnik': 'DBV', 'split': 'SPU', 'reykjavik': 'KEF',
  'porto': 'OPO', 'seville': 'SVQ', 'malaga': 'AGP',
  'palma': 'PMI', 'ibiza': 'IBZ', 'tenerife': 'TFS',
  'auckland': 'AKL', 'queenstown': 'ZQN', 'fiji': 'NAN',
  'tahiti': 'PPT', 'bora bora': 'BOB',
  // Safari, lodge, and remote destinations
  'kruger': 'MQP', 'kruger national park': 'MQP', 'mpumalanga': 'MQP',
  'sabi sand': 'MQP', 'sabi sand game reserve': 'MQP', 'sabi sands': 'MQP',
  'vilanculos': 'VNX', 'bazaruto': 'VNX', 'bazaruto island': 'VNX',
  'inhambane': 'INH', 'maputo': 'MPM', 'tofo': 'INH',
  'masai mara': 'NBO', 'maasai mara': 'NBO', 'serengeti': 'JRO',
  'ngorongoro': 'JRO', 'arusha': 'JRO', 'kilimanjaro': 'JRO',
  'victoria falls': 'VFA', 'livingstone': 'LVI',
  'okavango': 'MUB', 'okavango delta': 'MUB', 'maun': 'MUB',
  'windhoek': 'WDH', 'etosha': 'WDH', 'sossusvlei': 'WDH',
  'kigali': 'KGL', 'volcanoes national park': 'KGL',
};

/**
 * D8: Reverse lookup — get city name from an IATA airport code.
 * Built from CITY_TO_AIRPORT_CODE (picks the first/simplest city name per code).
 * @param {string} code - 3-letter IATA code (e.g. 'JFK')
 * @returns {string} City name or empty string
 */
const _AIRPORT_TO_CITY = (() => {
  const map = {};
  // Common abbreviations to skip in favor of full city names
  const ABBREVIATIONS = new Set(['nyc', 'la', 'sf', 'dc']);
  for (const [city, code] of Object.entries(CITY_TO_AIRPORT_CODE)) {
    const upper = code.toUpperCase();
    const isAbbrev = ABBREVIATIONS.has(city.toLowerCase()) || city.length <= 3;
    const existingIsAbbrev = map[upper] ? (ABBREVIATIONS.has(map[upper].raw) || map[upper].raw.length <= 3) : false;
    // Prefer non-abbreviated names; among non-abbreviated, prefer shorter (e.g., 'new york' over 'new york city')
    if (!map[upper] || (isAbbrev && !existingIsAbbrev ? false : (!isAbbrev && existingIsAbbrev) || city.length < map[upper].raw.length)) {
      map[upper] = {
        name: city.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        raw: city,
      };
    }
  }
  // Flatten to just names
  const result = {};
  for (const [code, val] of Object.entries(map)) {
    result[code] = val.name;
  }
  return result;
})();

export function reverseLookupAirportCity(code) {
  if (!code) return '';
  const upper = code.toUpperCase();
  // Two tables exist historically: DEPARTURE_AIRPORT_CITY_MAP (hand-maintained,
  // includes long-tail gateways like CHC, PUQ, USH, LYR) and _AIRPORT_TO_CITY
  // (auto-built from CITY_TO_AIRPORT_CODE). Check both so we don't miss IATA
  // codes that are only in one map. Hand-maintained wins on conflict.
  return DEPARTURE_AIRPORT_CITY_MAP[upper] || _AIRPORT_TO_CITY[upper] || '';
}

/**
 * Lookup airport code from city name string, stripping common suffixes.
 * @param {string} cityOrAirportStr
 * @param {string} [countryHint] - Optional country/region name to disambiguate
 *   ambiguous city prefixes (e.g. "Park City, UT" vs trans-ocean "PCP"). When
 *   provided, trans-ocean/small-territory airports in BLACKLISTED_PREFIX_AIRPORTS
 *   are only returned if the country hint matches that airport's country.
 * @returns {string} IATA code or empty string
 */
/**
 * Dynamic runtime cache for airport codes discovered via API/AI.
 * Populated by addAirportMapping() when searches resolve new cities.
 * This grows over the lifetime of the process — no TTL needed since airport codes don't change.
 */
const _dynamicAirportCache = {};

/**
 * Airport codes that are small, remote, or trans-ocean and MUST NOT be returned
 * for short/ambiguous prefix lookups unless a country hint confirms them.
 * This prevents "Park City" (Utah) or similar US/EU locations from accidentally
 * resolving to far-flung territory airports like PCP (Príncipe Airport, São Tomé).
 */
const BLACKLISTED_PREFIX_AIRPORTS = {
  PCP: ['são tomé', 'sao tome', 'sao tome and principe', 'são tomé and príncipe', 'principe'],
  SPR: ['belize'],          // Ambergris Caye/San Pedro, Belize
  CUK: ['belize'],          // Caye Caulker, Belize
  PLJ: ['belize'],          // Placencia, Belize
  PAR: ['france'],          // sometimes used for Paris metro area; not a real IATA
  NAS: ['bahamas'],
};

/**
 * Normalize a country/region hint for comparison.
 */
function _normalizeCountry(hint) {
  if (!hint) return '';
  return String(hint).toLowerCase().trim();
}

export function lookupCityAirportCode(cityOrAirportStr, countryHint) {
  if (!cityOrAirportStr) return '';
  const raw = String(cityOrAirportStr).toLowerCase();
  // Parse off any country/region suffix (after the comma) for country context
  const parts = raw.split(',').map(p => p.trim());
  const firstPart = parts[0] || '';
  const contextFromString = parts.slice(1).join(', '); // e.g. "utah" or "ut, usa"
  const effectiveCountryHint = _normalizeCountry(countryHint) || _normalizeCountry(contextFromString);

  // Strip airport-description suffixes BUT NOT the word "city" (which is part of
  // legitimate place names like "Park City", "Mexico City", "New York City").
  const cleaned = firstPart
    .replace(/\s*(airport|international|intl)\s*/gi, '')
    .trim();

  // Try the cleaned name first, then the original (with "city" intact), then
  // a version with "city" stripped (legacy behaviour for "Tokyo City" etc.).
  const candidates = [];
  if (cleaned) candidates.push(cleaned);
  const withoutCity = cleaned.replace(/\s*\bcity\b\s*/gi, ' ').replace(/\s+/g, ' ').trim();
  if (withoutCity && withoutCity !== cleaned) candidates.push(withoutCity);

  const _checkBlacklist = (code) => {
    const allowedCountries = BLACKLISTED_PREFIX_AIRPORTS[code];
    if (!allowedCountries) return true; // not blacklisted
    if (!effectiveCountryHint) return false; // blacklisted w/ no country → reject
    return allowedCountries.some(c => effectiveCountryHint.includes(c));
  };

  for (const key of candidates) {
    const staticMatch = CITY_TO_AIRPORT_CODE[key];
    if (staticMatch && _checkBlacklist(staticMatch)) return staticMatch;
    const dynamicMatch = _dynamicAirportCache[key];
    if (dynamicMatch && _checkBlacklist(dynamicMatch)) return dynamicMatch;
  }
  return '';
}

/**
 * Add a city→airport mapping to the runtime cache.
 * Called when airport search (Amadeus/AI) resolves a previously unknown city.
 * Also indexes common name variants (with/without hyphens, short forms).
 */
export function addAirportMapping(cityName, iataCode) {
  if (!cityName || !iataCode || iataCode.length !== 3) return;
  const lower = cityName.toLowerCase().trim();
  _dynamicAirportCache[lower] = iataCode.toUpperCase();
  // Also index without hyphens: "sharm el-sheikh" → "sharm el sheikh"
  const noHyphen = lower.replace(/-/g, ' ');
  if (noHyphen !== lower) _dynamicAirportCache[noHyphen] = iataCode.toUpperCase();
  // Index first word if city name is multi-word: "sharm el-sheikh" → "sharm"
  const firstWord = lower.split(/[\s-]/)[0];
  if (firstWord.length >= 4 && firstWord !== lower) {
    _dynamicAirportCache[firstWord] = iataCode.toUpperCase();
  }
}

// ─── Helper Functions ───────────────────────────────────────────────────────

/**
 * Safely extract city name from an airport object.
 * LLM/backend sometimes sets airport.city to an object instead of a string.
 *
 * @param {Object} airport - Airport object with .city property
 * @param {string} [fallback=''] - Fallback if city can't be extracted
 * @returns {string}
 */
export function safeAirportCity(airport, fallback = '') {
  if (!airport?.city) return fallback;
  if (typeof airport.city === 'string') return airport.city;
  return airport.city?.name || airport.city?.city || fallback;
}

/**
 * Clean a potentially messy IATA code from LLM responses.
 * e.g., "Multiple major airports (MAD, BCN, AGP-Málaga...)" → "MAD"
 *
 * @param {string} raw - Raw airport code string
 * @returns {string} Clean 3-letter IATA code
 */
/** Common 3-letter English words that are NOT IATA codes — prevents false matches in text */
const NON_IATA_WORDS = new Set([
  'FLY', 'THE', 'AND', 'FOR', 'ARE', 'BUT', 'NOT', 'YOU', 'ALL', 'CAN',
  'HER', 'WAS', 'ONE', 'OUR', 'OUT', 'DAY', 'HAD', 'HAS', 'HIS', 'HOW',
  'ITS', 'MAY', 'NEW', 'NOW', 'OLD', 'SEE', 'WAY', 'WHO', 'DID', 'GET',
  'LET', 'SAY', 'SHE', 'TOO', 'USE', 'VIA', 'AIR', 'TWO', 'BIG', 'END',
  'FAR', 'FEW', 'GOT', 'RUN', 'SET', 'TRY', 'WHY', 'ASK', 'MEN', 'RAN',
  'TOP', 'OFF', 'PUT', 'OWN', 'AGO', 'TAX', 'INTO',
]);

export function cleanIataCode(raw) {
  if (!raw) return '';
  if (/^[A-Z]{3}$/.test(raw)) return raw; // Already clean
  const upper = raw.toUpperCase();
  // Handle 4-letter FAA codes (e.g., "KMIA" → "MIA")
  if (/^[A-Z]{4}$/.test(upper) && upper.startsWith('K')) {
    return upper.substring(1);
  }
  // Find all 3-letter uppercase words and pick the first real IATA code
  const matches = upper.match(/\b[A-Z]{3}\b/g);
  if (matches) {
    for (const m of matches) {
      if (!NON_IATA_WORDS.has(m)) return m;
    }
  }
  return upper.substring(0, 3);
}

/**
 * Smart transport mode fallback based on geography.
 * Determines the most likely transport mode between two cities when
 * Haiku dynamic transport data isn't available.
 *
 * @param {string} fromCity
 * @param {string} toCity
 * @param {Object} helpers - Injected helper functions (to avoid closure dependencies)
 * @param {Function} helpers.getCityCountrySync - (cityName) => country
 * @param {Function} helpers.getContinentForCountry - (country) => continent
 * @param {Function} helpers.isIslandCity - (cityName) => boolean
 * @returns {string} 'flight' | 'car' | 'train'
 */
export function getSmartTransportMode(fromCity, toCity, helpers = {}) {
  const { getCityCountrySync: getCountry, getContinentForCountry: getContinent, isIslandCity: isIsland } = helpers;

  if (!getCountry || !getContinent || !isIsland) {
    // Without helpers, default to flight (safest guess for unknown routes)
    return 'flight';
  }

  const fromCountry = getCountry(fromCity);
  const toCountry = getCountry(toCity);
  const fromContinent = getContinent(fromCountry);
  const toContinent = getContinent(toCountry);

  // Different continents = always flight
  if (fromContinent && toContinent && fromContinent !== toContinent) {
    return 'flight';
  }

  // Different countries = default to flight (Haiku will override with train if appropriate)
  if (fromCountry && toCountry && fromCountry !== toCountry) {
    return 'flight';
  }

  // Same country — check if either city is an island (car impossible)
  if (isIsland(fromCity) || isIsland(toCity)) {
    return 'flight';
  }

  // Same country, not an island = default to car (Haiku will refine)
  return 'car';
}

/**
 * Frontend validation guard — prevent impossible transport modes from rendering.
 * Forces to flight when continents differ or when island cities are involved
 * (except ferry, which is valid between islands).
 *
 * @param {string} mode - Current transport mode
 * @param {string} fromCity - Origin city
 * @param {string} toCity - Destination city
 * @param {Object} helpers - { getCityCountrySync, getContinentForCountry, isIslandCity }
 * @returns {string} Validated transport mode (may be changed to 'flight')
 */
export function validateTransportMode(mode, fromCity, toCity, helpers = {}) {
  const { getCityCountrySync: getCountry, getContinentForCountry: getContinent, isIslandCity: isIsland } = helpers;
  if (!mode || mode === 'flight' || !getCountry || !getContinent || !isIsland) return mode;

  const fromCountry = getCountry(fromCity);
  const toCountry = getCountry(toCity);
  const fromContinent = getContinent(fromCountry);
  const toContinent = getContinent(toCountry);

  // Different continents = always impossible by car/bus/train
  if (fromContinent && toContinent && fromContinent !== toContinent) {
    return 'flight';
  }

  // Island destinations — driving/train impossible, but ferry IS valid
  if ((isIsland(fromCity) || isIsland(toCity)) && mode !== 'ferry') {
    return 'flight';
  }

  return mode;
}

// ─── Region-to-Station Mapping ──────────────────────────────────────────────

/** Maps region names to their nearest major train station. */
export const REGION_TO_STATION = {
  'tuscany': 'Florence SMN', 'provence': 'Avignon TGV', 'burgundy': 'Dijon',
  'cinque terre': 'La Spezia Centrale', 'amalfi': 'Naples Centrale', 'lake como': 'Milano Centrale',
  'amalfi coast': 'Naples Centrale', 'scottish highlands': 'Inverness', 'cotswolds': 'Moreton-in-Marsh',
};

/**
 * Resolve a city/region name to its nearest train station.
 *
 * @param {string} city - City or region name
 * @param {string} [fallback] - Fallback station name if no mapping found
 * @returns {string} Train station name
 */
export function resolveStation(city, fallback) {
  const lower = (city || '').toLowerCase().split(',')[0].trim();
  return REGION_TO_STATION[lower] || fallback;
}
